import ApplicationModel from "../../../shared/models/applications/api/ApplicationModel";
import { ApplicationModuleFieldLookup } from "../api/applicationModuleFields";
import {
    booleanAnswerToString,
    buildClosedGreeting,
    formatPhoneNumber,
    getFirstModuleFieldValue,
    getModuleFieldValue,
    getPhoneDigitsForSave,
    getSpecialDatesAnswerFromLookup,
    getWeeklyHoursAnswerFromLookup,
    getWeeklyHoursUpdates,
    inverseBooleanAnswerToString,
    OnboardingAnswerValue,
    parseBooleanString,
    parseSpecialDatesForSave,
    sanitizeMultilineText,
    WeeklyHoursAnswerValue
} from "../utils/onboardingFieldUtils";

export type OnboardingFlowType = "food-ordering" | "healthcare";
export type OnboardingQuestionInputType = "text" | "textarea" | "tel" | "hours" | "choice" | "select" | "email-list" | "currency" | "number" | "info" | "special-dates" | "procedure-types";

export interface OnboardingSaveInstruction {
    associatedVariable: string;
    value: string;
}

export interface OnboardingFlowBuildContext {
    application: ApplicationModel;
    moduleFieldLookup: ApplicationModuleFieldLookup;
}

export interface OnboardingQuestionConfig {
    id: string;
    title: string;
    helperText?: string;
    supportText?: string;
    tooltipText?: string;
    continueLabel?: string;
    skipLabel?: string;
    placeholder?: string;
    optional?: boolean;
    type: OnboardingQuestionInputType;
    section: string;
    associatedVariable?: string;
    associatedVariables?: string[];
    saveEnabled?: boolean;
    choices?: Array<{ label: string; value: string }>;
    allowMultipleChoices?: boolean;
    defaultValue?: string;
    getInitialValue?: (context: OnboardingFlowBuildContext) => OnboardingAnswerValue;
    normalizeInput?: (value: string) => string;
    isVisible?: (answers: Record<string, OnboardingAnswerValue>) => boolean;
    isValid?: (value: OnboardingAnswerValue) => boolean;
    getSaveInstructions?: (value: OnboardingAnswerValue, context: OnboardingFlowBuildContext) => OnboardingSaveInstruction[];
}

export interface OnboardingStepConfig {
    id: string;
    title: string;
    questions: OnboardingQuestionConfig[];
}

export interface OnboardingFlowConfig {
    type: OnboardingFlowType;
    title: string;
    steps: OnboardingStepConfig[];
}

const yesNoChoices = [
    { label: "Yes", value: "yes" },
    { label: "No", value: "no" }
];

const paymentChoices = [
    { label: "Prepaid", value: "prepaid" },
    { label: "Cash", value: "cash" },
    { label: "Card with driver", value: "card_with_driver" }
];

const multiChoiceDelimiter = ",";

const parseMultiChoiceValue = (value: string, choiceOrder: string[]): string[] => {
    const selected = new Set((value || "")
        .split(multiChoiceDelimiter)
        .map(entry => entry.trim())
        .filter(Boolean));

    return choiceOrder.filter(choice => selected.has(choice));
};

const serializeMultiChoiceValue = (values: string[], choiceOrder: string[]): string => {
    const selected = new Set(values);
    return choiceOrder.filter(choice => selected.has(choice)).join(multiChoiceDelimiter);
};

const usTimeZoneChoices = [
    { label: "US/Eastern", value: "US/Eastern" },
    { label: "US/Central", value: "US/Central" },
    { label: "US/Mountain", value: "US/Mountain" },
    { label: "US/Pacific", value: "US/Pacific" },
    { label: "US/Arizona", value: "US/Arizona" },
    { label: "US/Alaska", value: "US/Alaska" },
    { label: "US/Hawaii", value: "US/Hawaii" }
];

const timeZoneAliases: Record<string, string> = {
    "america/new_york": "US/Eastern",
    "america/detroit": "US/Eastern",
    "america/indiana/indianapolis": "US/Eastern",
    "us/eastern": "US/Eastern",
    "america/chicago": "US/Central",
    "us/central": "US/Central",
    "america/denver": "US/Mountain",
    "america/boise": "US/Mountain",
    "us/mountain": "US/Mountain",
    "america/los_angeles": "US/Pacific",
    "us/pacific": "US/Pacific",
    "america/phoenix": "US/Arizona",
    "us/arizona": "US/Arizona",
    "america/anchorage": "US/Alaska",
    "us/alaska": "US/Alaska",
    "pacific/honolulu": "US/Hawaii",
    "us/hawaii": "US/Hawaii"
};

const normalizeUsTimeZoneValue = (value: string): string => {
    const trimmed = (value || "").trim();
    if (!trimmed) {
        return "";
    }

    const normalized = timeZoneAliases[trimmed.toLowerCase()] || trimmed;
    return usTimeZoneChoices.some(choice => choice.value === normalized) ? normalized : "";
};

const greetingDefault = "Thanks for calling Voicify Pizza. Is this for pickup or delivery?";
const quickServeGreetingDefault = "Thanks for calling {LocationName} in {CityName}! Will this be for pickup or delivery?";
const fullServiceGreetingDefault = "Thanks for calling {LocationName} in {CityName}! I'm here to answer your questions or take your order. How can I help?";
const healthcareGreetingDefault = "Hi there and thank you for choosing {ClientName}. My name is {AssistantName}. I'm your Smart AI Assistant. I can help with scheduling appointments and answering general questions. Just so you know, this call is being recorded for quality purposes. How may I assist you?";

const allHoursProvided = (value: WeeklyHoursAnswerValue): boolean => {
    return [
        value.mondayHours,
        value.tuesdayHours,
        value.wednesdayHours,
        value.thursdayHours,
        value.fridayHours,
        value.saturdayHours,
        value.sundayHours
    ].every(dayValue => dayValue.trim().length > 0);
};

const stringQuestion = (config: Omit<OnboardingQuestionConfig, "type"> & { type?: OnboardingQuestionInputType }): OnboardingQuestionConfig => {
    const saveEnabled = config.saveEnabled !== false;

    return {
        saveEnabled,
        getInitialValue: ({ moduleFieldLookup }) => {
            const variables = config.associatedVariables || (config.associatedVariable ? [config.associatedVariable] : []);
            return variables.length > 0 ? getFirstModuleFieldValue(moduleFieldLookup, variables, config.defaultValue || "") : (config.defaultValue || "");
        },
        isValid: (value) => config.optional ? true : typeof value === "string" && value.trim().length > 0,
        getSaveInstructions: (value) => {
            if (!saveEnabled || !config.associatedVariable) {
                return [];
            }

            return [{
                associatedVariable: config.associatedVariable,
                value: typeof value === "string" ? value.trim() : ""
            }];
        },
        type: config.type || "text",
        ...config
    };
};

const notMappedQuestion = (config: Omit<OnboardingQuestionConfig, "saveEnabled" | "getSaveInstructions">): OnboardingQuestionConfig => ({
    ...config,
    saveEnabled: false,
    getSaveInstructions: () => []
});

const foodOrderingFlow: OnboardingFlowConfig = {
    type: "food-ordering",
    title: "Food ordering onboarding",
    steps: [
        {
            id: "general-info",
            title: "General Info",
            questions: [
                stringQuestion({
                    id: "storeName",
                    title: "Store name",
                    helperText: "Use the name customers know.",
                    placeholder: "Downtown Pizza",
                    section: "General Info",
                    associatedVariable: "{LocationName}",
                    getInitialValue: ({ application, moduleFieldLookup }) => getModuleFieldValue(moduleFieldLookup, "{LocationName}", application.name || "")
                }),
                notMappedQuestion(stringQuestion({
                    id: "address",
                    title: "Address",
                    helperText: "Your assistant uses this when people ask for directions.",
                    placeholder: "123 Main Street, Nashville, TN 37203",
                    section: "General Info",
                    associatedVariable: "__TODO_ADDRESS__"
                })),
                notMappedQuestion({
                    id: "posHoursInfo",
                    title: "Regular hours",
                    helperText: "Your assistant uses your POS hours. Take a moment now to make sure they're up to date.",
                    continueLabel: "POS Hours Are Up to Date",
                    section: "General Info",
                    type: "info",
                    optional: true,
                    defaultValue: ""
                }),
                stringQuestion({
                    id: "timeZone",
                    title: "Time zone",
                    helperText: "This helps your assistant answer hours correctly.",
                    section: "General Info",
                    type: "select",
                    choices: usTimeZoneChoices,
                    associatedVariable: "{TIME_ZONE}",
                    associatedVariables: ["{TIME_ZONE}", "{Webhook-Time-Zone}"],
                    getInitialValue: ({ moduleFieldLookup }) => normalizeUsTimeZoneValue(getFirstModuleFieldValue(moduleFieldLookup, ["{TIME_ZONE}", "{Webhook-Time-Zone}"], "")),
                    getSaveInstructions: (value) => {
                        const cleaned = normalizeUsTimeZoneValue(value as string);
                        return [
                            { associatedVariable: "{TIME_ZONE}", value: cleaned },
                            { associatedVariable: "{Webhook-Time-Zone}", value: cleaned }
                        ];
                    }
                }),
                {
                    id: "specialHours",
                    title: "Holiday closures",
                    helperText: "Tap any holidays you close for. We'll add every 2026-2030 date automatically.",
                    section: "General Info",
                    type: "special-dates",
                    optional: true,
                    saveEnabled: true,
                    associatedVariable: "DISABLED_DAYS",
                    getInitialValue: ({ moduleFieldLookup }) => getSpecialDatesAnswerFromLookup(moduleFieldLookup),
                    isValid: () => true,
                    getSaveInstructions: (value) => [{
                        associatedVariable: "DISABLED_DAYS",
                        value: parseSpecialDatesForSave(value as any)
                    }]
                }
            ]
        },
        {
            id: "phone-numbers",
            title: "Phone Numbers",
            questions: [
                notMappedQuestion({
                    ...stringQuestion({
                        id: "publicPhoneNumber",
                        title: "Public phone number",
                        supportText: "When we're ready to go live, customer calls here will be forwarded to your AI assistant.",
                        placeholder: "(555) 123-4567",
                        section: "Phone Numbers",
                        type: "tel",
                        associatedVariable: "__TODO_PUBLIC_PHONE__",
                        normalizeInput: formatPhoneNumber,
                        isValid: (value) => getPhoneDigitsForSave(value as string).length >= 10
                    }),
                    helperText: "This is the main number customers call."
                }),
                stringQuestion({
                    id: "transferPhoneNumber",
                    title: "Transfer phone number",
                    helperText: "Calls transfer here when someone needs a person.",
                    supportText: "This is your direct store line for human handoffs. We'll transfer customers here when needed. If you don't have a second number, your provider can help you configure one.",
                    skipLabel: "No second number yet",
                    placeholder: "(555) 987-6543",
                    section: "Phone Numbers",
                    type: "tel",
                    optional: true,
                    associatedVariable: "{{session.returntostorenumber}}",
                    normalizeInput: formatPhoneNumber,
                    isValid: (value) => getPhoneDigitsForSave(value as string).length >= 10,
                    getInitialValue: ({ moduleFieldLookup }) => formatPhoneNumber(getModuleFieldValue(moduleFieldLookup, "{{session.returntostorenumber}}")),
                    getSaveInstructions: (value) => [{
                        associatedVariable: "{{session.returntostorenumber}}",
                        value: getPhoneDigitsForSave(value as string)
                    }]
                })
            ]
        },
        {
            id: "greetings",
            title: "Greetings",
            questions: [
                {
                    id: "greetingMode",
                    title: "Default greeting",
                    section: "Greetings",
                    type: "choice",
                    choices: [
                        { label: "Quick-serve / carryout & delivery", value: "quick-serve" },
                        { label: "Full-service / dine-in", value: "full-service" },
                        { label: "Use my own greeting", value: "use-own" }
                    ],
                    defaultValue: "quick-serve",
                    saveEnabled: true,
                    isValid: (value) => value === "quick-serve" || value === "full-service" || value === "use-own",
                    getInitialValue: () => "quick-serve",
                    normalizeInput: undefined,
                    getSaveInstructions: (value) => {
                        if (value === "use-own") {
                            return [];
                        }

                        const template = value === "full-service" ? fullServiceGreetingDefault : quickServeGreetingDefault;
                        const cleaned = sanitizeMultilineText(template);
                        return [
                            { associatedVariable: "{Item-Welcome-Message-New-User-Response}", value: cleaned },
                            { associatedVariable: "{Item-Welcome-Message-Repeat-User-Response-1}", value: cleaned },
                            { associatedVariable: "{Item-Welcome-Message-Repeat-User-Response-2}", value: cleaned },
                            { associatedVariable: "{Item-Welcome-Message-Repeat-User-Response-3}", value: cleaned },
                            { associatedVariable: "{CLOSED_WELCOME_MESSAGE}", value: buildClosedGreeting(cleaned) },
                            { associatedVariable: "{Item-Welcome-Message-Closed-For-Day-Response}", value: buildClosedGreeting(cleaned) }
                        ];
                    }
                },
                {
                    ...stringQuestion({
                        id: "englishGreeting",
                        title: "Write your greeting",
                        helperText: "End your greeting with a question so callers know the assistant is ready to help.",
                        placeholder: greetingDefault,
                        section: "Greetings",
                        type: "textarea",
                        associatedVariable: "{Item-Welcome-Message-New-User-Response}",
                        associatedVariables: [
                            "{Item-Welcome-Message-New-User-Response}",
                            "{Item-Welcome-Message-Repeat-User-Response-1}",
                            "{Item-Welcome-Message-Repeat-User-Response-2}",
                            "{Item-Welcome-Message-Repeat-User-Response-3}"
                        ],
                        defaultValue: greetingDefault,
                        isVisible: () => false,
                        getSaveInstructions: (value) => {
                            const cleaned = sanitizeMultilineText(value as string);
                            return [
                                { associatedVariable: "{Item-Welcome-Message-New-User-Response}", value: cleaned },
                                { associatedVariable: "{Item-Welcome-Message-Repeat-User-Response-1}", value: cleaned },
                                { associatedVariable: "{Item-Welcome-Message-Repeat-User-Response-2}", value: cleaned },
                                { associatedVariable: "{Item-Welcome-Message-Repeat-User-Response-3}", value: cleaned },
                                { associatedVariable: "{CLOSED_WELCOME_MESSAGE}", value: buildClosedGreeting(cleaned) },
                                { associatedVariable: "{Item-Welcome-Message-Closed-For-Day-Response}", value: buildClosedGreeting(cleaned) }
                            ];
                        }
                    }),
                    isVisible: () => false
                }
            ]
        },
        {
            id: "delivery-payments",
            title: "Delivery & Payments",
            questions: [
                stringQuestion({
                    id: "allowDelivery",
                    title: "Should your assistant allow delivery orders?",
                    helperText: "Let callers place delivery orders by phone.",
                    section: "Delivery & Payments",
                    type: "choice",
                    associatedVariable: "{Webhook-Allow-Delivery}",
                    choices: yesNoChoices,
                    getInitialValue: ({ moduleFieldLookup }) => parseBooleanString(getModuleFieldValue(moduleFieldLookup, "{Webhook-Allow-Delivery}"), "no"),
                    getSaveInstructions: (value) => [{ associatedVariable: "{Webhook-Allow-Delivery}", value: booleanAnswerToString(value as string) }]
                }),
                stringQuestion({
                    id: "deliveryMinimumEnabled",
                    title: "Require a delivery minimum",
                    helperText: "Say yes if delivery orders need a minimum amount.",
                    section: "Delivery & Payments",
                    type: "choice",
                    isVisible: (answers) => answers.allowDelivery === "yes",
                    associatedVariable: "{Webhook-Disable-Minimum-Charge-Prompt}",
                    choices: yesNoChoices,
                    getInitialValue: ({ moduleFieldLookup }) => parseBooleanString(getModuleFieldValue(moduleFieldLookup, "{Webhook-Disable-Minimum-Charge-Prompt}"), "no") === "yes" ? "no" : "yes",
                    getSaveInstructions: (value) => [{ associatedVariable: "{Webhook-Disable-Minimum-Charge-Prompt}", value: inverseBooleanAnswerToString(value as string) }]
                }),
                stringQuestion({
                    id: "deliveryMinimumAmount",
                    title: "Delivery minimum amount",
                    helperText: "Enter the minimum order amount if you use one.",
                    placeholder: "$15.00",
                    section: "Delivery & Payments",
                    type: "currency",
                    isVisible: (answers) => answers.allowDelivery === "yes" && answers.deliveryMinimumEnabled === "yes",
                    associatedVariable: "{Webhook-Delivery-Order-Minimum-Charge}",
                    optional: true
                }),
                notMappedQuestion(stringQuestion({
                    id: "deliveryPaymentMethod",
                    title: "Delivery payment method",
                    supportText: "Select all of the ways customers can pay for delivery.",
                    section: "Delivery & Payments",
                    type: "choice",
                    allowMultipleChoices: true,
                    isVisible: (answers) => answers.allowDelivery === "yes",
                    choices: paymentChoices,
                    defaultValue: serializeMultiChoiceValue(["prepaid"], paymentChoices.map(choice => choice.value)),
                    isValid: (value) => parseMultiChoiceValue(value as string, paymentChoices.map(choice => choice.value)).length > 0
                })),
                notMappedQuestion(stringQuestion({
                    id: "pickupPrepaidThreshold",
                    title: "Pickup prepaid threshold",
                    helperText: "Require payment in advance for pickup orders over this amount.",
                    supportText: "Require payment in advance for pickup orders over this amount.",
                    placeholder: "$125.00",
                    section: "Delivery & Payments",
                    type: "currency",
                    associatedVariable: "__TODO_PICKUP_PREPAID_THRESHOLD__",
                    defaultValue: "$125.00",
                    optional: true
                })),
                stringQuestion({
                    id: "allowFutureOrders",
                    title: "Allow future orders",
                    supportText: "Let customers schedule orders ahead of time.",
                    section: "Delivery & Payments",
                    type: "choice",
                    associatedVariable: "{Webhook-Allow-Scheduling-For-Future-Date}",
                    choices: yesNoChoices,
                    getInitialValue: ({ moduleFieldLookup }) => parseBooleanString(getModuleFieldValue(moduleFieldLookup, "{Webhook-Allow-Scheduling-For-Future-Date}"), "no"),
                    getSaveInstructions: (value) => [{ associatedVariable: "{Webhook-Allow-Scheduling-For-Future-Date}", value: booleanAnswerToString(value as string) }]
                })
            ]
        },
        {
            id: "sales-upsells",
            title: "Sales, Upsells, Add-Ons",
            questions: [
                stringQuestion({
                    id: "upsellsBeforeCheckout",
                    title: "Upsells",
                    supportText: "List any items you'd like the assistant to suggest before checkout if the caller doesn't have them in their cart. For example, you might want to suggest jalepeno poppers and garlic bread.",
                    helperText: "What are some items you'd like the assistant to upsell before checkout if they don't have them in their cart?",
                    placeholder: "jalepeno poppers, salads, french fries",
                    section: "Sales, Upsells, Add-Ons",
                    type: "textarea",
                    associatedVariable: "{UPSELL_PROMPT}"
                }),
                notMappedQuestion(stringQuestion({
                    id: "popularItems",
                    title: "Popular items to mention",
                    supportText: "Tell us what you want the assistant to recommend. \"Our most popular item is the pepperoni pizza, and we highly recommend trying our new siracha wings.\"",
                    placeholder: "Our best seller is the pepperoni pizza. The buffalo chicken pizza is another favorite.",
                    section: "Sales, Upsells, Add-Ons",
                    type: "textarea",
                    associatedVariable: "__TODO_POPULAR_ITEMS__"
                })),
                stringQuestion({
                    id: "hasCoupons",
                    title: "Allow coupons in POS to be applied by assistant",
                    supportText: "Choose yes if the assistant should apply coupons from your POS.",
                    section: "Sales, Upsells, Add-Ons",
                    type: "choice",
                    associatedVariable: "{Webhook-Allow-Promotions}",
                    choices: yesNoChoices,
                    getInitialValue: ({ moduleFieldLookup }) => parseBooleanString(getModuleFieldValue(moduleFieldLookup, "{Webhook-Allow-Promotions}"), "no"),
                    getSaveInstructions: (value) => [{ associatedVariable: "{Webhook-Allow-Promotions}", value: booleanAnswerToString(value as string) }]
                }),
                stringQuestion({
                    id: "autoApplyDeals",
                    title: "Should the assistant automatically apply deals the caller's order qualifies for?",
                    supportText: "Choose yes if qualifying deals should be applied automatically without the caller asking for them.",
                    section: "Sales, Upsells, Add-Ons",
                    type: "choice",
                    isVisible: (answers) => answers.hasCoupons === "yes",
                    associatedVariable: "{Webhook-Disable-Automatic-Coupons}",
                    choices: yesNoChoices,
                    getInitialValue: ({ moduleFieldLookup }) => parseBooleanString(getModuleFieldValue(moduleFieldLookup, "{Webhook-Disable-Automatic-Coupons}"), "no") === "yes" ? "no" : "yes",
                    getSaveInstructions: (value) => [{ associatedVariable: "{Webhook-Disable-Automatic-Coupons}", value: inverseBooleanAnswerToString(value as string) }]
                })
            ]
        },
        {
            id: "more-info",
            title: "More Info",
            questions: [
                stringQuestion({
                    id: "locationFaq",
                    title: "Common questions the assistant should know the answer to",
                    supportText: "Add common questions and answers the assistant should know for this location. \"Parking is behind the building. This location does not have a drive-thru.\"",
                    placeholder: "Parking is behind the building. This location does not have a drive-thru.",
                    section: "More Info",
                    type: "textarea",
                    associatedVariable: "{LOCATION_SPECIFIC_OTHER_QUESTION_INFO}",
                    optional: true
                }),
                notMappedQuestion(stringQuestion({
                    id: "specialNotes",
                    title: "Anything else we should know",
                    supportText: "Add anything helpful for the team here.",
                    placeholder: "We close early when severe weather is expected.",
                    section: "More Info",
                    type: "textarea",
                    associatedVariable: "__TODO_SPECIAL_NOTES__",
                    optional: true
                }))
            ]
        }
    ]
};

const healthcareFlow: OnboardingFlowConfig = {
    type: "healthcare",
    title: "Healthcare onboarding",
    steps: [
        {
            id: "basic-info",
            title: "Basic Info",
            questions: [
                notMappedQuestion(stringQuestion({
                    id: "firstName",
                    title: "Your first name",
                    helperText: "Who should we list as the main onboarding contact?",
                    placeholder: "Taylor",
                    section: "Basic Info",
                    associatedVariable: "__TODO_CONTACT_FIRST_NAME__"
                })),
                notMappedQuestion(stringQuestion({
                    id: "lastName",
                    title: "Your last name",
                    helperText: "Who should we list as the main onboarding contact?",
                    placeholder: "Smith",
                    section: "Basic Info",
                    associatedVariable: "__TODO_CONTACT_LAST_NAME__"
                })),
                notMappedQuestion(stringQuestion({
                    id: "contactEmail",
                    title: "Your email",
                    helperText: "We will use this if we need to follow up.",
                    placeholder: "office@example.com",
                    section: "Basic Info",
                    type: "text",
                    associatedVariable: "__TODO_CONTACT_EMAIL__"
                })),
                stringQuestion({
                    id: "officeName",
                    title: "Office name",
                    helperText: "Use the name patients know.",
                    placeholder: "Smile Dental Care",
                    section: "Basic Info",
                    associatedVariable: "{ClientName}",
                    getInitialValue: ({ application, moduleFieldLookup }) => getModuleFieldValue(moduleFieldLookup, "{ClientName}", application.name || "")
                }),
                stringQuestion({
                    id: "assistantName",
                    title: "Assistant name",
                    helperText: "This is the name your AI assistant will introduce itself with.",
                    placeholder: "Linda",
                    section: "Basic Info",
                    defaultValue: "Linda",
                    associatedVariable: "{AssistantName}",
                    getInitialValue: ({ moduleFieldLookup }) => getModuleFieldValue(moduleFieldLookup, "{AssistantName}", "Linda")
                })
            ]
        },
        {
            id: "contact-info",
            title: "Contact Info",
            questions: [
                stringQuestion({
                    id: "transferPhoneNumber",
                    title: "Transfer phone number",
                    helperText: "This is the main office number for transfers.",
                    supportText: "This is your direct store line for human handoffs. We'll transfer customers here when needed. If you don't have a second number, your provider can help you configure one.",
                    skipLabel: "No second number yet",
                    placeholder: "(555) 123-4567",
                    section: "Contact Info",
                    type: "tel",
                    optional: true,
                    associatedVariable: "{TransferTarget}",
                    normalizeInput: formatPhoneNumber,
                    isValid: (value) => getPhoneDigitsForSave(value as string).length >= 10,
                    getInitialValue: ({ moduleFieldLookup }) => formatPhoneNumber(getModuleFieldValue(moduleFieldLookup, "{TransferTarget}")),
                    getSaveInstructions: (value) => [{ associatedVariable: "{TransferTarget}", value: getPhoneDigitsForSave(value as string) }]
                }),
                stringQuestion({
                    id: "emergencyTransferPhoneNumber",
                    title: "Emergency transfer phone number",
                    helperText: "Use a backup number for urgent calls.",
                    placeholder: "(555) 555-1212",
                    section: "Contact Info",
                    type: "tel",
                    associatedVariable: "{EmergencyTransferTarget}",
                    normalizeInput: formatPhoneNumber,
                    isValid: (value) => getPhoneDigitsForSave(value as string).length >= 10,
                    getInitialValue: ({ moduleFieldLookup }) => formatPhoneNumber(getModuleFieldValue(moduleFieldLookup, "{EmergencyTransferTarget}")),
                    getSaveInstructions: (value) => [{ associatedVariable: "{EmergencyTransferTarget}", value: getPhoneDigitsForSave(value as string) }]
                }),
                stringQuestion({
                    id: "appointmentEmails",
                    title: "Appointment and message email recipients",
                    supportText: "Enter the office email addresses where you'd like appointment confirmations, left messages, and dropped call summaries sent. This is usually your main office email. Add yourself if you'd like to see emails coming through from Voicify.",
                    placeholder: "frontdesk@example.com",
                    section: "Contact Info",
                    type: "email-list",
                    associatedVariable: "{{OfficeAppointmentToEmail}}",
                    getInitialValue: ({ moduleFieldLookup }) => {
                        const raw = getModuleFieldValue(moduleFieldLookup, "{{OfficeAppointmentToEmail}}", "");
                        return raw.trim() || "";
                    },
                    getSaveInstructions: (value) => {
                        const emails = (value as string)
                            .split(",")
                            .map(e => e.trim())
                            .filter(Boolean)
                            .join(", ");
                        return [{ associatedVariable: "{{OfficeAppointmentToEmail}}", value: emails }];
                    }
                })
            ]
        },
        {
            id: "greetings",
            title: "Greetings",
            questions: [
                {
                    id: "greetingMode",
                    title: "Default greeting",
                    supportText: healthcareGreetingDefault,
                    section: "Greetings",
                    type: "choice",
                    choices: [
                        { label: "Use the default greeting", value: "use-default" },
                        { label: "Use my own greeting", value: "use-own" }
                    ],
                    defaultValue: "use-default",
                    saveEnabled: true,
                    isValid: (value) => value === "use-default" || value === "use-own",
                    getInitialValue: () => "use-default",
                    normalizeInput: undefined,
                    getSaveInstructions: (value) => {
                        if (value !== "use-default") {
                            return [];
                        }

                        const cleaned = sanitizeMultilineText(healthcareGreetingDefault);
                        return [
                            { associatedVariable: "{Item-Welcome-Message-New-User-Response}", value: cleaned },
                            { associatedVariable: "{Item-Welcome-Message-Repeat-User-Response-1}", value: cleaned },
                            { associatedVariable: "{Item-Welcome-Message-Repeat-User-Response-2}", value: cleaned },
                            { associatedVariable: "{Item-Welcome-Message-Repeat-User-Response-3}", value: cleaned },
                            { associatedVariable: "{CLOSED_WELCOME_MESSAGE}", value: buildClosedGreeting(cleaned) },
                            { associatedVariable: "{Item-Welcome-Message-Closed-For-Day-Response}", value: buildClosedGreeting(cleaned) }
                        ];
                    }
                },
                {
                    ...stringQuestion({
                        id: "englishGreeting",
                        title: "Write your greeting",
                        helperText: "End your greeting with a form of \"How may I assist you?\" so callers know the assistant is ready to help.",
                        placeholder: healthcareGreetingDefault,
                        section: "Greetings",
                        type: "textarea",
                        associatedVariable: "{Item-Welcome-Message-New-User-Response}",
                        associatedVariables: [
                            "{Item-Welcome-Message-New-User-Response}",
                            "{Item-Welcome-Message-Repeat-User-Response-1}",
                            "{Item-Welcome-Message-Repeat-User-Response-2}",
                            "{Item-Welcome-Message-Repeat-User-Response-3}"
                        ],
                        defaultValue: healthcareGreetingDefault,
                        isVisible: () => false,
                        getSaveInstructions: (value) => {
                            const cleaned = sanitizeMultilineText(value as string);
                            return [
                                { associatedVariable: "{Item-Welcome-Message-New-User-Response}", value: cleaned },
                                { associatedVariable: "{Item-Welcome-Message-Repeat-User-Response-1}", value: cleaned },
                                { associatedVariable: "{Item-Welcome-Message-Repeat-User-Response-2}", value: cleaned },
                                { associatedVariable: "{Item-Welcome-Message-Repeat-User-Response-3}", value: cleaned },
                                { associatedVariable: "{CLOSED_WELCOME_MESSAGE}", value: buildClosedGreeting(cleaned) },
                                { associatedVariable: "{Item-Welcome-Message-Closed-For-Day-Response}", value: buildClosedGreeting(cleaned) }
                            ];
                        }
                    }),
                    isVisible: () => false
                }
            ]
        },
        {
            id: "insurance",
            title: "Insurance",
            questions: [
                notMappedQuestion(stringQuestion({
                    id: "insuranceAccepted",
                    title: "Insurance accepted",
                    supportText: "List all insurance providers you accept.",
                    placeholder: "Delta Dental, Aetna, Cigna",
                    section: "Insurance",
                    type: "textarea",
                    associatedVariable: "__TODO_INSURANCE_ACCEPTED__"
                })),
                notMappedQuestion(stringQuestion({
                    id: "insuranceNotAccepted",
                    title: "Insurance not accepted",
                    supportText: "List all insurance providers you don't accept.",
                    placeholder: "Medicaid",
                    section: "Insurance",
                    type: "textarea",
                    associatedVariable: "__TODO_INSURANCE_NOT_ACCEPTED__"
                })),
                notMappedQuestion(stringQuestion({
                    id: "insuranceOutOfNetwork",
                    title: "Insurance out of network",
                    supportText: "List all insurance providers who are out of network.",
                    placeholder: "Guardian",
                    section: "Insurance",
                    type: "textarea",
                    associatedVariable: "__TODO_INSURANCE_OUT_OF_NETWORK__"
                }))
            ]
        },
        {
            id: "policies",
            title: "Policies",
            questions: [
                {
                    id: "insuranceRulesMode",
                    title: "Insurance rules",
                    supportText: "Specify any rules or exceptions regarding insurance.",
                    section: "Policies",
                    type: "choice",
                    choices: [
                        { label: "No rules", value: "no-rules" },
                        { label: "Yes, we have special rules", value: "has-rules" }
                    ],
                    defaultValue: "no-rules",
                    saveEnabled: true,
                    isValid: (value) => value === "no-rules" || value === "has-rules",
                    getInitialValue: () => "no-rules",
                    normalizeInput: undefined,
                    getSaveInstructions: () => []
                },
                {
                    ...stringQuestion({
                        id: "insuranceRules",
                        title: "Insurance rules",
                        placeholder: "If the patient is unsure, we transfer them to the front desk.",
                        section: "Policies",
                        type: "textarea",
                        associatedVariable: "{Custom-Insurance-Rules}",
                        isVisible: () => false
                    }),
                    isVisible: () => false
                },
                stringQuestion({
                    id: "cancellationPolicy",
                    title: "Cancellation policy",
                    supportText: "Include the exact wording you'd like the assistant to use when responding to questions about your cancellation policy.",
                    placeholder: "Please give at least 24 hours notice.",
                    section: "Policies",
                    type: "textarea",
                    associatedVariable: "{{CancellationPolicy}}"
                }),
                {
                    id: "cancellationFeeMode",
                    title: "Cancellation fee",
                    section: "Policies",
                    type: "choice",
                    choices: [
                        { label: "No fee", value: "no-fee" },
                        { label: "Yes, we charge a fee", value: "has-fee" }
                    ],
                    defaultValue: "no-fee",
                    saveEnabled: true,
                    isValid: (value) => value === "no-fee" || value === "has-fee",
                    getInitialValue: () => "no-fee",
                    normalizeInput: undefined,
                    getSaveInstructions: () => []
                },
                {
                    ...stringQuestion({
                        id: "cancellationFee",
                        title: "Cancellation fee",
                        placeholder: "50",
                        section: "Policies",
                        type: "currency",
                        associatedVariable: "{CancelationFee}",
                        isVisible: () => false
                    }),
                    isVisible: () => false
                },
                {
                    id: "cancellationWindowMode",
                    title: "Cancellation window",
                    section: "Policies",
                    type: "choice",
                    choices: [
                        { label: "No window", value: "no-window" },
                        { label: "Yes, we have a cancellation window", value: "has-window" }
                    ],
                    defaultValue: "no-window",
                    saveEnabled: true,
                    isValid: (value) => value === "no-window" || value === "has-window",
                    getInitialValue: () => "no-window",
                    normalizeInput: undefined,
                    getSaveInstructions: () => []
                },
                {
                    ...stringQuestion({
                        id: "cancellationWindowDays",
                        title: "Cancellation window in days",
                        placeholder: "1",
                        helperText: "We track cancellation windows by days, not hours.",
                        section: "Policies",
                        type: "number",
                        associatedVariable: "{NonCancellableWindowDaysPrompt}",
                        isVisible: () => false,
                        isValid: (value) => typeof value === "string" && /^\d+$/.test(value.trim())
                    }),
                    isVisible: () => false
                },
            ]
        },
        {
            id: "payment-appointments",
            title: "Payment & Appointments",
            questions: [
                stringQuestion({
                    id: "walkInsAccepted",
                    title: "Accept walk-ins",
                    helperText: "Say yes if patients can come in without an appointment.",
                    section: "Payment & Appointments",
                    type: "choice",
                    associatedVariable: "{WalkInsMessage}",
                    choices: yesNoChoices,
                    getInitialValue: ({ moduleFieldLookup }) => parseBooleanString(getModuleFieldValue(moduleFieldLookup, "{WalkInsMessage}"), "no"),
                    getSaveInstructions: (value) => [{ associatedVariable: "{WalkInsMessage}", value: booleanAnswerToString(value as string) }]
                }),
                {
                    id: "billingPricingMode",
                    title: "Billing and pricing questions",
                    supportText: "How should billing or pricing questions be handled?",
                    section: "Payment & Appointments",
                    type: "choice",
                    choices: [
                        { label: "Yes, transfer to office", value: "transfer" },
                        { label: "No, don't transfer", value: "no-transfer" }
                    ],
                    defaultValue: "transfer",
                    saveEnabled: true,
                    isValid: (value) => value === "transfer" || value === "no-transfer",
                    getInitialValue: () => "transfer",
                    normalizeInput: undefined,
                    getSaveInstructions: () => []
                },
                {
                    ...stringQuestion({
                        id: "billingPricingInfo",
                        title: "Billing and pricing questions",
                        helperText: "Include the exact wording you'd like the assistant to use when responding to questions about your billing and pricing.",
                        placeholder: "For billing questions, please visit our website or speak with our front desk during office hours.",
                        section: "Payment & Appointments",
                        type: "textarea",
                        associatedVariable: "{BillingInfo}",
                        associatedVariables: ["{BillingInfo}", "{PricingInformation}"],
                        isVisible: () => false,
                        getSaveInstructions: (value) => {
                            const cleaned = sanitizeMultilineText(value as string);
                            return [
                                { associatedVariable: "{BillingInfo}", value: cleaned },
                                { associatedVariable: "{PricingInformation}", value: cleaned }
                            ];
                        }
                    }),
                    isVisible: () => false
                },
                stringQuestion({
                    id: "paymentPlanInfo",
                    title: "Payment plan information",
                    supportText: "Describe any payment plans or options you offer.",
                    placeholder: "We offer CareCredit and split-pay options.",
                    section: "Payment & Appointments",
                    type: "textarea",
                    associatedVariable: "{PaymentPlansPrompt}"
                }),
                notMappedQuestion({
                    id: "procedureTypes",
                    title: "Procedure types",
                    helperText: "List the types of procedures (i.e. prophy, perio etc.) your assistant should handle, including those specifically for new patients, existing patients, children, etc. If you use Denticon PMS, these procedures need to be visible in AppointNow.",
                    supportText: "List the types of procedures (i.e. prophy, perio etc.) your assistant should handle, including those specifically for new patients, existing patients, children, etc. If you use Denticon PMS, these procedures need to be visible in AppointNow.",
                    section: "Payment & Appointments",
                    type: "procedure-types",
                    associatedVariable: "__TODO_PROCEDURE_TYPES__",
                    saveEnabled: false,
                    optional: true,
                    getInitialValue: () => [],
                    isValid: (value) => Array.isArray(value) && value.every(entry => {
                        const description = typeof entry?.procedureTypeDescription === "string" ? entry.procedureTypeDescription.trim() : "";
                        const duration = entry?.appointmentDuration;
                        return description.length > 0 && duration !== undefined && duration !== null && !Number.isNaN(Number(duration));
                    })
                })
            ]
        },
        {
            id: "hours-other",
            title: "Hours & Other",
            questions: [
                {
                    id: "weeklyHours",
                    title: "Regular hours",
                    supportText: "These are your standard weekly hours. You can add specific closures like holidays or occasional Fridays, for example, on the next page.",
                    section: "Hours & Other",
                    type: "hours",
                    saveEnabled: true,
                    getInitialValue: ({ moduleFieldLookup }) => getWeeklyHoursAnswerFromLookup(moduleFieldLookup),
                    isValid: (value) => !!value && typeof value === "object" && allHoursProvided(value as WeeklyHoursAnswerValue),
                    getSaveInstructions: (value) => getWeeklyHoursUpdates(value as WeeklyHoursAnswerValue)
                },
                {
                    id: "specialHours",
                    title: "Holiday & other closures",
                    helperText: "Select the holidays you close for — we'll automatically include all dates from 2026–2030. Need to add a one-off closure, like every other Friday you're closed? Set your regular Friday hours on the previous page, then add those specific dates below.",
                    section: "Hours & Other",
                    type: "special-dates",
                    optional: true,
                    saveEnabled: true,
                    associatedVariable: "DISABLED_DAYS",
                    getInitialValue: ({ moduleFieldLookup }) => getSpecialDatesAnswerFromLookup(moduleFieldLookup),
                    isValid: () => true,
                    getSaveInstructions: (value) => [{
                        associatedVariable: "DISABLED_DAYS",
                        value: parseSpecialDatesForSave(value as any)
                    }]
                },
                notMappedQuestion(stringQuestion({
                    id: "additionalNotes",
                    title: "Anything else you would like to share",
                    helperText: "",
                    supportText: "Your AI Dental Assistant runs on a proven, standardized workflow. If you have any special instructions or preferences, feel free to share them here. We’ll do our best to accommodate them when we can!",
                    placeholder: "Please let patients know we validate parking after 3 PM.",
                    section: "Hours & Other",
                    type: "textarea",
                    associatedVariable: "__TODO_ADDITIONAL_NOTES__",
                    optional: true
                }))
            ]
        }
    ]
};

export const onboardingFlows: Record<OnboardingFlowType, OnboardingFlowConfig> = {
    "food-ordering": foodOrderingFlow,
    healthcare: healthcareFlow
};

export const getOnboardingFlow = (type: OnboardingFlowType): OnboardingFlowConfig => onboardingFlows[type];
